import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { 
  CheckCircle, 
  XCircle, 
  Clock, 
  User,
  Mail,
  Calendar,
  Eye,
  Edit,
  Trash2,
  Users,
  Shield,
  BarChart3,
  Plus,
  Search,
  Filter,
  Package,
  DollarSign,
  Palette,
  Image,
  FileText,
  TrendingUp
} from 'lucide-react';

const DesignerDashboard = () => {
  const { user, isDesigner } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [projects, setProjects] = useState([]);
  const [designs, setDesigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  // Mock data for designer
  useEffect(() => {
    const mockProjects = [
      {
        id: '1',
        name: 'Logo Design for Tech Startup',
        client: 'TechCorp Inc.',
        clientEmail: 'client1@gmail.com',
        status: 'in-progress',
        deadline: '2024-02-01T00:00:00Z',
        budget: 500,
        createdAt: '2024-01-15T10:30:00Z'
      },
      {
        id: '2',
        name: 'Website UI/UX Design',
        client: 'E-commerce Store',
        clientEmail: 'client2@gmail.com',
        status: 'completed',
        deadline: '2024-01-20T00:00:00Z',
        budget: 1200,
        createdAt: '2024-01-10T11:45:00Z'
      },
      {
        id: '3',
        name: 'Mobile App Design',
        client: 'Fitness App',
        clientEmail: 'client3@gmail.com',
        status: 'pending',
        deadline: '2024-02-15T00:00:00Z',
        budget: 800,
        createdAt: '2024-01-12T14:20:00Z'
      }
    ];

    const mockDesigns = [
      {
        id: '1',
        name: 'Logo Design Concept 1',
        projectId: '1',
        type: 'logo',
        status: 'approved',
        createdAt: '2024-01-15T10:30:00Z',
        image: 'https://via.placeholder.com/150'
      },
      {
        id: '2',
        name: 'Website Homepage Design',
        projectId: '2',
        type: 'website',
        status: 'completed',
        createdAt: '2024-01-10T11:45:00Z',
        image: 'https://via.placeholder.com/150'
      },
      {
        id: '3',
        name: 'Mobile App Wireframe',
        projectId: '3',
        type: 'mobile',
        status: 'draft',
        createdAt: '2024-01-12T14:20:00Z',
        image: 'https://via.placeholder.com/150'
      }
    ];

    setTimeout(() => {
      setProjects(mockProjects);
      setDesigns(mockDesigns);
      setLoading(false);
    }, 1000);
  }, []);

  const handleUpdateProject = (projectId, updatedData) => {
    setProjects(prev => prev.map(project => 
      project.id === projectId ? { ...project, ...updatedData } : project
    ));
    alert('Đã cập nhật dự án thành công!');
  };

  const handleDeleteProject = (projectId) => {
    if (window.confirm('Bạn có chắc chắn muốn xóa dự án này?')) {
      setProjects(prev => prev.filter(project => project.id !== projectId));
      alert('Đã xóa dự án thành công!');
    }
  };

  const handleUpdateDesignStatus = (designId, newStatus) => {
    setDesigns(prev => prev.map(design => 
      design.id === designId ? { ...design, status: newStatus } : design
    ));
    alert(`Đã cập nhật trạng thái thiết kế thành ${newStatus}!`);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('vi-VN');
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND'
    }).format(price * 1000);
  };

  const filteredProjects = projects.filter(project => {
    const matchesSearch = project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         project.client.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || project.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const stats = [
    {
      name: 'Tổng dự án',
      value: projects.length.toString(),
      change: '+3%',
      changeType: 'positive',
      icon: FileText
    },
    {
      name: 'Thiết kế hoàn thành',
      value: designs.filter(d => d.status === 'completed').length.toString(),
      change: '+15%',
      changeType: 'positive',
      icon: Palette
    },
    {
      name: 'Doanh thu tháng',
      value: formatPrice(projects.reduce((sum, project) => sum + project.budget, 0)),
      change: '+8%',
      changeType: 'positive',
      icon: DollarSign
    },
    {
      name: 'Tỷ lệ hoàn thành',
      value: `${Math.round((projects.filter(p => p.status === 'completed').length / projects.length) * 100)}%`,
      change: '+5%',
      changeType: 'positive',
      icon: TrendingUp
    }
  ];

  const projectStats = {
    totalProjects: projects.length,
    pendingProjects: projects.filter(p => p.status === 'pending').length,
    inProgressProjects: projects.filter(p => p.status === 'in-progress').length,
    completedProjects: projects.filter(p => p.status === 'completed').length
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <h1 className="text-2xl font-bold text-blue-600">Designer Dashboard</h1>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-3">
                {user?.avatar ? (
                  <img
                    src={user.avatar}
                    alt={user.name}
                    className="h-8 w-8 rounded-full"
                  />
                ) : (
                  <div className="h-8 w-8 rounded-full bg-blue-600 flex items-center justify-center">
                    <span className="text-sm font-medium text-white">
                      {user?.name?.charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}
                <div className="hidden md:block">
                  <p className="text-sm font-medium text-gray-700">{user?.name}</p>
                  <p className="text-xs text-gray-500">Designer</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs */}
        <div className="mb-8">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('overview')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'overview'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <BarChart3 className="inline-block w-4 h-4 mr-2" />
                Tổng quan
              </button>
              <button
                onClick={() => setActiveTab('projects')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'projects'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <FileText className="inline-block w-4 h-4 mr-2" />
                Dự án ({projects.length})
              </button>
              <button
                onClick={() => setActiveTab('designs')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'designs'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Palette className="inline-block w-4 h-4 mr-2" />
                Thiết kế ({designs.length})
              </button>
            </nav>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <>
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="space-y-6">
                {/* Stats */}
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
                  {stats.map((stat) => {
                    const Icon = stat.icon;
                    return (
                      <div key={stat.name} className="bg-white overflow-hidden shadow rounded-lg">
                        <div className="p-5">
                          <div className="flex items-center">
                            <div className="flex-shrink-0">
                              <Icon className="h-6 w-6 text-gray-400" />
                            </div>
                            <div className="ml-4 w-0 flex-1">
                              <dl>
                                <dt className="text-sm font-medium text-gray-500 truncate">
                                  {stat.name}
                                </dt>
                                <dd className="flex items-baseline">
                                  <div className="text-2xl font-semibold text-gray-900">
                                    {stat.value}
                                  </div>
                                  <div className={`ml-2 flex items-baseline text-sm font-semibold ${
                                    stat.changeType === 'positive' ? 'text-green-600' : 'text-red-600'
                                  }`}>
                                    {stat.change}
                                  </div>
                                </dd>
                              </dl>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Project Statistics */}
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-4">
                  <div className="bg-white overflow-hidden shadow rounded-lg">
                    <div className="p-5">
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          <FileText className="h-6 w-6 text-blue-500" />
                        </div>
                        <div className="ml-5 w-0 flex-1">
                          <dl>
                            <dt className="text-sm font-medium text-gray-500 truncate">Tổng dự án</dt>
                            <dd className="text-lg font-medium text-gray-900">{projectStats.totalProjects}</dd>
                          </dl>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white overflow-hidden shadow rounded-lg">
                    <div className="p-5">
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          <Clock className="h-6 w-6 text-yellow-500" />
                        </div>
                        <div className="ml-5 w-0 flex-1">
                          <dl>
                            <dt className="text-sm font-medium text-gray-500 truncate">Chờ xử lý</dt>
                            <dd className="text-lg font-medium text-gray-900">{projectStats.pendingProjects}</dd>
                          </dl>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white overflow-hidden shadow rounded-lg">
                    <div className="p-5">
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          <Palette className="h-6 w-6 text-purple-500" />
                        </div>
                        <div className="ml-5 w-0 flex-1">
                          <dl>
                            <dt className="text-sm font-medium text-gray-500 truncate">Đang thực hiện</dt>
                            <dd className="text-lg font-medium text-gray-900">{projectStats.inProgressProjects}</dd>
                          </dl>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white overflow-hidden shadow rounded-lg">
                    <div className="p-5">
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          <CheckCircle className="h-6 w-6 text-green-500" />
                        </div>
                        <div className="ml-5 w-0 flex-1">
                          <dl>
                            <dt className="text-sm font-medium text-gray-500 truncate">Đã hoàn thành</dt>
                            <dd className="text-lg font-medium text-gray-900">{projectStats.completedProjects}</dd>
                          </dl>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Recent Projects */}
                <div className="bg-white shadow rounded-lg">
                  <div className="px-4 py-5 sm:p-6">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Dự án gần đây</h3>
                    <div className="space-y-4">
                      {projects.slice(0, 5).map((project) => (
                        <div key={project.id} className="flex items-center space-x-3">
                          <div className="flex-shrink-0">
                            <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                              <FileText className="h-4 w-4 text-blue-600" />
                            </div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900">
                              {project.name} - {project.client}
                            </p>
                            <p className="text-sm text-gray-500">
                              {formatPrice(project.budget)} - Deadline: {formatDate(project.deadline)}
                            </p>
                          </div>
                          <div className="flex-shrink-0">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              project.status === 'completed' ? 'bg-green-100 text-green-800' : 
                              project.status === 'in-progress' ? 'bg-blue-100 text-blue-800' : 'bg-yellow-100 text-yellow-800'
                            }`}>
                              {project.status === 'completed' ? 'Hoàn thành' : 
                               project.status === 'in-progress' ? 'Đang thực hiện' : 'Chờ xử lý'}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Projects Tab */}
            {activeTab === 'projects' && (
              <div className="space-y-6">
                {/* Search and Filter */}
                <div className="bg-white shadow rounded-lg p-6">
                  <div className="flex flex-col sm:flex-row gap-4">
                    <div className="flex-1">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                          type="text"
                          placeholder="Tìm kiếm dự án..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                    </div>
                    <div className="flex-shrink-0">
                      <select
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                        className="px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="all">Tất cả trạng thái</option>
                        <option value="pending">Chờ xử lý</option>
                        <option value="in-progress">Đang thực hiện</option>
                        <option value="completed">Hoàn thành</option>
                      </select>
                    </div>
                    <div className="flex-shrink-0">
                      <button className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700">
                        <Plus className="h-4 w-4 mr-2" />
                        Tạo dự án mới
                      </button>
                    </div>
                  </div>
                </div>

                {/* Projects List */}
                <div className="bg-white shadow overflow-hidden sm:rounded-md">
                  <div className="px-4 py-5 sm:px-6">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Danh sách Dự án ({filteredProjects.length})
                    </h3>
                  </div>
                  <ul className="divide-y divide-gray-200">
                    {filteredProjects.map((project) => (
                      <li key={project.id}>
                        <div className="px-4 py-4 sm:px-6">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center">
                              <div className="flex-shrink-0">
                                <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                                  <FileText className="h-6 w-6 text-blue-600" />
                                </div>
                              </div>
                              <div className="ml-4">
                                <div className="flex items-center">
                                  <p className="text-sm font-medium text-gray-900">{project.name}</p>
                                  <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                    project.status === 'completed' ? 'bg-green-100 text-green-800' : 
                                    project.status === 'in-progress' ? 'bg-blue-100 text-blue-800' : 'bg-yellow-100 text-yellow-800'
                                  }`}>
                                    {project.status === 'completed' ? 'Hoàn thành' : 
                                     project.status === 'in-progress' ? 'Đang thực hiện' : 'Chờ xử lý'}
                                  </span>
                                </div>
                                <div className="flex items-center mt-1">
                                  <User className="h-4 w-4 text-gray-400 mr-1" />
                                  <p className="text-sm text-gray-500">{project.client}</p>
                                </div>
                                <div className="flex items-center mt-1">
                                  <DollarSign className="h-4 w-4 text-gray-400 mr-1" />
                                  <p className="text-sm text-gray-500">
                                    {formatPrice(project.budget)} - Deadline: {formatDate(project.deadline)}
                                  </p>
                                </div>
                                <div className="flex items-center mt-1">
                                  <Calendar className="h-4 w-4 text-gray-400 mr-1" />
                                  <p className="text-xs text-gray-500">
                                    Tạo: {formatDate(project.createdAt)}
                                  </p>
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => handleUpdateProject(project.id, { status: 'in-progress' })}
                                className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                              >
                                <Edit className="h-4 w-4 mr-1" />
                                Sửa
                              </button>
                              <button
                                onClick={() => handleDeleteProject(project.id)}
                                className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                              >
                                <Trash2 className="h-4 w-4 mr-1" />
                                Xóa
                              </button>
                            </div>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {/* Designs Tab */}
            {activeTab === 'designs' && (
              <div className="space-y-6">
                {/* Designs List */}
                <div className="bg-white shadow overflow-hidden sm:rounded-md">
                  <div className="px-4 py-5 sm:px-6">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Danh sách Thiết kế ({designs.length})
                    </h3>
                  </div>
                  <ul className="divide-y divide-gray-200">
                    {designs.map((design) => (
                      <li key={design.id}>
                        <div className="px-4 py-4 sm:px-6">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center">
                              <div className="flex-shrink-0">
                                <img
                                  src={design.image}
                                  alt={design.name}
                                  className="h-12 w-12 rounded-lg object-cover"
                                />
                              </div>
                              <div className="ml-4">
                                <div className="flex items-center">
                                  <p className="text-sm font-medium text-gray-900">{design.name}</p>
                                  <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                    design.status === 'completed' ? 'bg-green-100 text-green-800' : 
                                    design.status === 'approved' ? 'bg-blue-100 text-blue-800' : 'bg-yellow-100 text-yellow-800'
                                  }`}>
                                    {design.status === 'completed' ? 'Hoàn thành' : 
                                     design.status === 'approved' ? 'Đã duyệt' : 'Bản thảo'}
                                  </span>
                                </div>
                                <div className="flex items-center mt-1">
                                  <Palette className="h-4 w-4 text-gray-400 mr-1" />
                                  <p className="text-sm text-gray-500">{design.type}</p>
                                </div>
                                <div className="flex items-center mt-1">
                                  <Calendar className="h-4 w-4 text-gray-400 mr-1" />
                                  <p className="text-xs text-gray-500">
                                    {formatDate(design.createdAt)}
                                  </p>
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              {design.status === 'draft' && (
                                <>
                                  <button
                                    onClick={() => handleUpdateDesignStatus(design.id, 'approved')}
                                    className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                  >
                                    <CheckCircle className="h-4 w-4 mr-1" />
                                    Gửi duyệt
                                  </button>
                                  <button
                                    onClick={() => handleUpdateDesignStatus(design.id, 'completed')}
                                    className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                                  >
                                    <CheckCircle className="h-4 w-4 mr-1" />
                                    Hoàn thành
                                  </button>
                                </>
                              )}
                              {design.status === 'approved' && (
                                <button
                                  onClick={() => handleUpdateDesignStatus(design.id, 'completed')}
                                  className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                                >
                                  <CheckCircle className="h-4 w-4 mr-1" />
                                  Hoàn thành
                                </button>
                              )}
                              <button className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                                <Eye className="h-4 w-4 mr-1" />
                                Xem chi tiết
                              </button>
                            </div>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default DesignerDashboard; 